import { useCallback } from 'react'
import { tv } from 'tailwind-variants'
import { UIButton } from 'src/components/ui/Button'
import { track } from 'src/utils/tracker'
import type { CardStateResult } from './useMembershipCardState'

export interface MembershipCardButtonProps {
  cardState: CardStateResult
  loading?: boolean
  onAction?: () => Promise<void>
  trackingArgs?: Record<string, any>
}

const buttonStyles = tv({
  base: 'w-full m-3 px-8 py-4 leading-none grid place-content-center font-medium rounded-lg transition',
  variants: {
    type: {
      primary:
        'bg-[--accent-color] hover:bg-[--accent-color-dark] dark:hover:bg-[--accent-color-dark] text-[--accent-color-text]',
      secondary:
        'hover:bg-[--accent-hover-color] ring-1 ring-inset ring-[--accent-color] text-[--accent-color] dark:text-[--accent-color-dark]',
      danger: 'bg-red-500 hover:bg-red-600 text-white dark:text-white',
      disabled:
        'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed',
    },
    disabled: {
      true: 'disabled:opacity-50 disabled:pointer-events-none',
    },
  },
})

export function MembershipCardButton({
  cardState,
  onAction,
  trackingArgs,
}: MembershipCardButtonProps) {
  const handleClick = useCallback(async () => {
    // Always track join_member_click first (to match original component)
    track('join_member_click', trackingArgs)

    // Then execute the action which may trigger additional tracking
    if (!cardState.isDisabled) {
      await onAction?.()
    }
  }, [cardState.isDisabled, onAction, trackingArgs])

  return (
    <>
      <div className="flex-1 pt-4 pb-2 flex flex-col justify-end text-center">
        {cardState.disabledReason && (
          <p className="px-4 mlg:px-8 text-sm font-medium text-balance text-black dark:text-white">
            {cardState.disabledReason}
          </p>
        )}
      </div>
      <div className="flex bg-white dark:bg-zinc-700 sticky bottom-0 w-full rounded-b-2xl">
        <UIButton
          disabled={cardState.isDisabled}
          busy="auto"
          className={buttonStyles({
            type: cardState.buttonType,
            disabled: cardState.isDisabled,
          })}
          variant="ghost"
          color={cardState.buttonType === 'danger' ? 'danger' : undefined}
          onPress={handleClick}
        >
          <span className="capitalize">{cardState.label}</span>
        </UIButton>
      </div>
    </>
  )
}
